import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { getRouteUrl } from '@utils/urls';

import { BUTTON_VARIANTS } from '@common/components/Button';
import { HOME } from '@common/routes';
import Layout from '@common/containers/Layout';
import RouteLayout from '@common/containers/RouteLayout';
import ImageContentSection, {
  Title,
  Lead,
} from '@common/components/ImageContentSection';
import DeepLinks from '@common/components/DeepLinks';
import { useDevice } from '@common/hooks/useDevice';

import { ButtonsWrapper, StyledButtonLink } from './GameLanding.styled';

const imageBaseProps = {
  breakpoints: [1601, 1367, 1201, 1200, 1025, 801, 641, 481, 376, 320],
  widths: [770, 700, 600, 520, 1090, 920, 720, 640, 480, 375],
  aspectRatios: [
    '19:20',
    '19:20',
    '19:20',
    '19:20',
    '4:2.6',
    '4:3.2',
    '4:3.4',
    '4:3.6',
    '3.6:4',
    '3.6:4',
  ],
};

export const GameLanding = ({ pageData: { title, lead, image, meta } }) => {
  const { locale, asPath } = useRouter();
  const { t } = useTranslation();
  const { iOS, android, isMobile } = useDevice();

  return (
    <Fragment>
      <DeepLinks />
      <Layout
        description={meta.description}
        ogImage={image.url}
        pageType="gameLandingPage"
        title={meta.title}
        canonicalPageUrl={`/${locale}${asPath}`}
      >
        <RouteLayout
          backRoute={HOME}
          backRouteAs={getRouteUrl(locale, HOME)}
          columns={12}
          stickyTitle={title}
          isMobileHero
        >
          <ImageContentSection
            breakpoints={imageBaseProps.breakpoints}
            widths={imageBaseProps.widths}
            aspectRatios={imageBaseProps.aspectRatios}
            src={image?.url}
            alt={image?.altText}
          >
            <Title>{title}</Title>
            <Lead>{lead}</Lead>
            <ButtonsWrapper>
              {(iOS || !isMobile) && (
                <StyledButtonLink
                  variant={BUTTON_VARIANTS.LEVEL_1_GREEN}
                  href={t(
                    'global.links.appDownload.appStore',
                    'apple.com/app-store/',
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  isExternal={false}
                >
                  {t('global.button.appDownload.appStore.title', 'Apple Store')}
                </StyledButtonLink>
              )}
              {(android || !isMobile) && (
                <StyledButtonLink
                  variant={BUTTON_VARIANTS.LEVEL_1_GREEN}
                  href={t('global.links.appDownload.googlePlay', 'google.com')}
                  target="_blank"
                  rel="noopener noreferrer"
                  isExternal={false}
                >
                  {t(
                    'global.button.appDownload.googlePlay.title',
                    'Google Play',
                  )}
                </StyledButtonLink>
              )}
            </ButtonsWrapper>
          </ImageContentSection>
        </RouteLayout>
      </Layout>
    </Fragment>
  );
};

GameLanding.propTypes = {
  pageData: PropTypes.shape({
    body: PropTypes.arrayOf(PropTypes.shape({})),
    image: PropTypes.shape({
      altText: PropTypes.string,
      url: PropTypes.string,
    }),
    lead: PropTypes.string,
    meta: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    }).isRequired,
    subtitle: PropTypes.string,
    title: PropTypes.string.isRequired,
  }).isRequired,
};

export default GameLanding;
